import { useRef, useState } from "react";
import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import OliverText from "@builder/components/general/OliverText";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { DynamicData, DynamicDataKey } from "@common/build/dynamicData";
import { ButtonNode, TextNode, TimeFormat } from "@common/build/types";
import { formatTime } from "@common/utils/dynamicDataHelpers";
import "@builder/components/general/SelectInput/style.scss";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

const EditTime = ({ parent }: EditableProps) => {
    const node = useAppSelector(getSelectedNode) as TextNode | ButtonNode;
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const selectRef = useRef<HTMLInputElement>(null);

    const isDynamicDataTime = node?.dynamicData && DynamicData[node.dynamicData as DynamicDataKey].dataType === "time";
    const options: { value: TimeFormat; label: string }[] = [
        { value: "relative", label: t("sidebarCollapse.Relative time (12 minutes ago)") },
        { value: "HH:mm", label: t("sidebarCollapse.24-hour format (15:30)") },
        { value: "hh:mm a", label: t("sidebarCollapse.12-hour with AM/PM (03:30 PM)") },
        { value: "HH:mm:ss", label: t("sidebarCollapse.With seconds (15:30:45)") },
        { value: "hh:mm:ss a", label: t("sidebarCollapse.12-hour with seconds (03:30:45 PM)") },
        { value: "dd/MM/yyyy", label: t("sidebarCollapse.Day-first date (30/12/2023)") },
        { value: "MM/dd/yyyy", label: t("sidebarCollapse.Month-first date (12/31/2023)") },
        { value: "MM/dd/yyyy HH:mm", label: t("sidebarCollapse.US DateTime (12/31/2023 15:30)") },
        { value: "PPPP", label: t("sidebarCollapse.Full date (Sunday, December 31st, 2023)") },
        { value: "PP", label: t("sidebarCollapse.Medium date (Dec 31, 2023)") },
        { value: "p", label: t("sidebarCollapse.Time (12:00 PM)") },
        { value: "Pp", label: t("sidebarCollapse.Short dateTime (12/31/2023, 3:30 PM)") },
        { value: "yyyy-MM-dd", label: t("sidebarCollapse.ISO Date (2023-12-31)") },
        { value: "yyyy-MM-dd HH:mm:ss", label: t("sidebarCollapse.ISO DateTime (2023-12-31 15:30:00)") },
        { value: "do MMMM, yyyy", label: t("sidebarCollapse.Verbal date (31st December, 2023)") },
        { value: "MMMM do, yyyy", label: t("sidebarCollapse.Verbal date alternative (December 31st, 2023)") },
        { value: "dd MMM yyyy", label: t("sidebarCollapse.Abbreviated date (31 Dec 2023)") },
        { value: "EEE, MMM d, yyyy", label: t("sidebarCollapse.Weekday date (Sun, Dec 31, 2023)") },
        { value: "dd-MM-yyyy HH:mm", label: t("sidebarCollapse.DateTime combined (30-12-2023 15:30)") }
      ]      
    
    const currentTimeFormat = options.find(option => option.value === (node.timeFormat || (node.dynamicData === "time" ? "HH:mm" : "dd/MM/yyyy")));

    

    if (!isDynamicDataTime) return null;

    const onSelect = (e: React.MouseEvent) => {
        const value = e.currentTarget.getAttribute("data-value");
        if (value) {
            dispatch(updateNodeProperty({ propertyPath: ["timeFormat"], value }));
            setIsOpen(false);
        }
    };

    return (
        <SidebarCollapse title="Time format" parent={parent}>
            <OliverText size="xs" text={`${t("sidebarCollapse.Example")}: ${formatTime(new Date(), currentTimeFormat?.value || "HH:mm")}`} />
            <div className="select-input-wrapper layout-row" >
                <div className={classNames("select-wrapper", { "select-active": isOpen })} ref={selectRef} style={{width: "100%"}}>
                    <img src="/angled-arrow-down.svg" alt="dropdown icon" />
                    <input type="text" value={currentTimeFormat?.label || ""} readOnly onClick={() => setIsOpen(!isOpen)} style={isOpen ? { borderRadius: "0 0 8px 8px" } : {}} />
                    {isOpen && (
                        <div className="select-input-options" style={{ position: "absolute", bottom: "100%", left: 0, zIndex: 9999 }}>
                            {options.map(({ value, label }, index) => (
                                <div className="select-input-option" key={index} data-value={value} onClick={onSelect} style={{paddingBlock: "1.3rem", borderTop: "1px solid #e1e1e1", borderBottom: 0, ...(index === 0 && { borderRadius: "8px 8px 0 0" }), ...(index === options.length - 1 && { borderRadius: 0 })}}>
                                    {label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </SidebarCollapse>
    );
};

export default EditTime;